import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardShell from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDays, Plus, Check, Trash2, Loader2, Clock, AlertCircle, ChevronLeft, ChevronRight, List, LayoutGrid, Sparkles, Wand2 } from "lucide-react";

const ALL_PLATFORMS = [
  { id: "tiktok", name: "TikTok" }, { id: "instagram", name: "Instagram" },
  { id: "youtube", name: "YouTube" }, { id: "facebook", name: "Facebook" },
];

const Calendar = () => {
  const { user } = useAuth();
  const platforms = user?.platforms?.length ? user.platforms.map((p) => ({ id: p.platform, name: ALL_PLATFORMS.find((x) => x.id === p.platform)?.name || p.platform })) : ALL_PLATFORMS;
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ platform: platforms[0]?.id || "tiktok", title: "", notes: "", scheduled_at: "" });
  const [view, setView] = useState("list");
  const [weekOffset, setWeekOffset] = useState(0);
  const [busyAutofill, setBusyAutofill] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [bestTimes, setBestTimes] = useState(null);
  const [btPlatform, setBtPlatform] = useState(platforms[0]?.id || "tiktok");
  const [btLoading, setBtLoading] = useState(false);

  const parseSlot = (slot) => {
    const wd = (slot.toLowerCase().match(/(sun|mon|tue|wed|thu|fri|sat)/) || [])[1];
    const t = (slot.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)/i) || [])[0];
    return { wd, label: (t || slot).trim() };
  };

  const loadBestTimes = async () => {
    setBtLoading(true);
    try {
      const { data } = await api.post("/ai/besttime", { platform: btPlatform, topic: "-" });
      const map = {};
      (data.result?.best_times || []).forEach((s) => {
        const { wd, label } = parseSlot(s);
        if (wd) (map[wd] = map[wd] || []).push(label);
      });
      setBestTimes(map);
      setView("week");
      toast.success("Best posting times mapped onto your week ✨");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load best times"); }
    finally { setBtLoading(false); }
  };

  const autofillWeek = async () => {
    setBusyAutofill(true);
    try {
      const { data } = await api.post("/ai/autofill-week", { platform: platforms[0]?.id || "tiktok" });
      const msg = data.created > 0
        ? `Added ${data.created} AI post idea${data.created === 1 ? "" : "s"}${data.skipped ? ` · skipped ${data.skipped} day${data.skipped === 1 ? "" : "s"} you'd already planned` : ""}!`
        : "Your week is already fully planned — nothing to add!";
      toast.success(msg);
      setView("week"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Autofill failed"); }
    finally { setBusyAutofill(false); }
  };

  const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
  const suggestTime = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post("/ai/besttime", { platform: form.platform, topic: "-" });
      const slot = (data.result?.best_times || [])[0] || "";
      const m = slot.toLowerCase().match(/(sun|mon|tue|wed|thu|fri|sat)/);
      const hm = slot.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
      const now = new Date();
      const target = new Date(now);
      if (m) { const wd = WEEKDAYS.indexOf(m[1]); let add = (wd - now.getDay() + 7) % 7; if (add === 0) add = 7; target.setDate(now.getDate() + add); }
      else target.setDate(now.getDate() + 1);
      let h = hm ? parseInt(hm[1], 10) : 18; const mm = hm && hm[2] ? parseInt(hm[2], 10) : 0;
      if (hm && hm[3]) { if (hm[3].toLowerCase() === "pm" && h < 12) h += 12; if (hm[3].toLowerCase() === "am" && h === 12) h = 0; }
      target.setHours(h, mm, 0, 0);
      const local = new Date(target.getTime() - target.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setForm((f) => ({ ...f, scheduled_at: local }));
      toast.success(`Best time: ${slot || "next evening"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not suggest a time"); }
    finally { setSuggesting(false); }
  };

  const load = () => api.get("/calendar").then((r) => setPosts(r.data.posts)).catch(() => toast.error("Could not load calendar")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.scheduled_at) { toast.error("Add a title and date/time"); return; }
    try {
      await api.post("/calendar", { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() });
      toast.success("Post scheduled!");
      setOpen(false); setForm({ platform: platforms[0]?.id || "tiktok", title: "", notes: "", scheduled_at: "" });
      load();
    } catch { toast.error("Could not schedule"); }
  };

  const togglePosted = async (p) => {
    const status = p.status === "posted" ? "planned" : "posted";
    setPosts((x) => x.map((q) => q.id === p.id ? { ...q, status } : q));
    try { await api.patch(`/calendar/${p.id}`, { status }); } catch { toast.error("Could not update"); load(); }
  };

  const remove = async (id) => {
    setPosts((x) => x.filter((p) => p.id !== id));
    try { await api.delete(`/calendar/${id}`); toast.success("Removed"); } catch { load(); }
  };

  const now = new Date();
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endToday = new Date(startToday.getTime() + 86400000);
  const group = (p) => {
    if (p.status === "posted") return "posted";
    const d = new Date(p.scheduled_at);
    if (d < startToday) return "overdue";
    if (d < endToday) return "today";
    return "upcoming";
  };
  const groups = { overdue: [], today: [], upcoming: [], posted: [] };
  posts.forEach((p) => groups[group(p)].push(p));

  const SECTIONS = [
    { key: "overdue", label: "Overdue", icon: AlertCircle, color: "text-secondary" },
    { key: "today", label: "Due today", icon: Clock, color: "text-chart-4" },
    { key: "upcoming", label: "Upcoming", icon: CalendarDays, color: "text-primary" },
    { key: "posted", label: "Posted", icon: Check, color: "text-muted-foreground" },
  ];

  const mondayOf = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x; };
  const weekStart = mondayOf(new Date(Date.now() + weekOffset * 7 * 86400000));
  const weekDays = Array.from({ length: 7 }, (_, i) => new Date(weekStart.getTime() + i * 86400000));
  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const postsByDay = {};
  posts.forEach((p) => { const k = dayKey(new Date(p.scheduled_at)); (postsByDay[k] = postsByDay[k] || []).push(p); });
  const openNew = (d) => {
    const dt = new Date(d); dt.setHours(12, 0, 0, 0);
    const local = new Date(dt.getTime() - dt.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    setForm({ platform: platforms[0]?.id || "tiktok", title: "", notes: "", scheduled_at: local }); setOpen(true);
  };
  const todayKey = dayKey(new Date());

  const Card = ({ p }) => (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border border-border/50 bg-card p-4 flex items-start justify-between gap-3" data-testid={`calendar-post-${p.id}`}>
      <div className="min-w-0">
        <div className={`font-semibold ${p.status === "posted" ? "line-through text-muted-foreground" : ""}`}>{p.title}</div>
        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
          <Badge className="bg-primary/10 text-primary border border-primary/20 capitalize text-[10px]">{p.platform}</Badge>
          {new Date(p.scheduled_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
        </div>
        {p.notes && <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.notes}</p>}
      </div>
      <div className="flex gap-1 shrink-0">
        <Button variant="ghost" size="sm" onClick={() => togglePosted(p)} className={p.status === "posted" ? "text-primary" : "text-muted-foreground"} data-testid={`calendar-toggle-${p.id}`}><Check className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => remove(p.id)} className="text-secondary" data-testid={`calendar-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </motion.div>
  );

  return (
    <DashboardShell>
      <div className="p-6 md:p-10 max-w-4xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center"><CalendarDays className="w-5 h-5" /></div>
              <h1 className="font-heading text-3xl font-black tracking-tight">Content Calendar</h1>
            </div>
            <p className="text-muted-foreground mt-2">Plan your posts, track what's due, and check them off once published.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={autofillWeek} disabled={busyAutofill} className="rounded-full border-primary/30 text-primary" data-testid="autofill-week-btn">
              {busyAutofill ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> AI autofill week</>}
            </Button>
            <Button onClick={() => setOpen(true)} className="rounded-full bg-primary text-background font-bold" data-testid="new-post-btn"><Plus className="w-4 h-4 mr-1" /> Schedule post</Button>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="inline-flex rounded-full border border-white/10 p-1">
            <button onClick={() => setView("list")} className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${view === "list" ? "bg-primary text-background" : "text-muted-foreground"}`} data-testid="view-list-btn"><List className="w-3.5 h-3.5" /> List</button>
            <button onClick={() => setView("week")} className={`px-4 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5 ${view === "week" ? "bg-primary text-background" : "text-muted-foreground"}`} data-testid="view-week-btn"><LayoutGrid className="w-3.5 h-3.5" /> Week</button>
          </div>
          {view === "week" && (
            <div className="flex items-center gap-3 flex-wrap">
              <Select value={btPlatform} onValueChange={setBtPlatform}>
                <SelectTrigger className="h-8 w-32 bg-transparent border-white/10 rounded-full text-xs" data-testid="best-times-platform"><SelectValue /></SelectTrigger>
                <SelectContent>{platforms.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={loadBestTimes} disabled={btLoading} className={`text-xs flex items-center gap-1 rounded-full border px-3 py-1.5 ${bestTimes ? "border-primary/40 text-primary bg-primary/10" : "border-white/10 text-muted-foreground"}`} data-testid="best-times-btn">
                {btLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Best times
              </button>
              {bestTimes && <button onClick={() => setBestTimes(null)} className="text-xs text-muted-foreground" data-testid="best-times-clear">Clear</button>}
              <button onClick={() => setWeekOffset((w) => w - 1)} className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/5" data-testid="week-prev-btn"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-medium min-w-[150px] text-center">{weekDays[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} – {weekDays[6].toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
              <button onClick={() => setWeekOffset((w) => w + 1)} className="w-8 h-8 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/5" data-testid="week-next-btn"><ChevronRight className="w-4 h-4" /></button>
              {weekOffset !== 0 && <button onClick={() => setWeekOffset(0)} className="text-xs text-primary" data-testid="week-today-btn">Today</button>}
            </div>
          )}
        </div>

        {view === "week" ? (
          <div className="grid grid-cols-1 sm:grid-cols-7 gap-2" data-testid="week-grid">
            {weekDays.map((d) => {
              const k = dayKey(d);
              const dayPosts = (postsByDay[k] || []).sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
              const isToday = k === todayKey;
              const wdKey = WEEKDAYS[d.getDay()];
              const dayBest = bestTimes?.[wdKey];
              return (
                <div key={k} className={`rounded-xl border p-2 min-h-[140px] ${dayBest?.length ? "border-primary/50 bg-primary/[0.07]" : isToday ? "border-primary/40 bg-primary/5" : "border-border/50 bg-card"}`} data-testid={`week-day-${k}`}>
                  <div className="flex items-center justify-between px-1 mb-2">
                    <div className={`text-xs font-semibold ${isToday ? "text-primary" : "text-muted-foreground"}`}>{d.toLocaleDateString(undefined, { weekday: "short" })} {d.getDate()}</div>
                    <button onClick={() => openNew(d)} className="text-muted-foreground hover:text-primary" data-testid={`week-add-${k}`}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  {dayBest?.length > 0 && (
                    <div className="flex flex-wrap gap-1 px-1 mb-2" data-testid={`best-times-day-${k}`}>
                      {dayBest.map((t, i) => (
                        <span key={i} className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-primary bg-primary/15 border border-primary/30 rounded-full px-1.5 py-0.5">
                          <Sparkles className="w-2.5 h-2.5" />{t}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="space-y-1.5">
                    {dayPosts.map((p) => (
                      <div key={p.id} onClick={() => togglePosted(p)} title="Click to toggle posted"
                        className={`cursor-pointer rounded-lg px-2 py-1.5 text-xs border ${p.status === "posted" ? "border-primary/30 bg-primary/10 line-through text-muted-foreground" : "border-white/10 bg-white/5"}`} data-testid={`week-post-${p.id}`}>
                        <div className="font-medium truncate">{p.title}</div>
                        <div className="text-[10px] text-muted-foreground">{new Date(p.scheduled_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })} · {p.platform}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
        ) : posts.length === 0 ? (
          <div className="rounded-2xl border border-border/50 bg-card p-12 text-center text-muted-foreground" data-testid="calendar-empty">
            No posts scheduled. Click "Schedule post" to plan your first one.
          </div>
        ) : (
          <div className="space-y-8">
            {SECTIONS.map((s) => groups[s.key].length > 0 && (
              <div key={s.key} data-testid={`calendar-section-${s.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <s.icon className={`w-4 h-4 ${s.color}`} />
                  <h3 className="font-heading font-semibold">{s.label}</h3>
                  <span className="text-xs text-muted-foreground">({groups[s.key].length})</span>
                </div>
                <div className="space-y-2">{groups[s.key].map((p) => <Card key={p.id} p={p} />)}</div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader><DialogTitle className="font-heading">Schedule a post</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Platform</Label>
                  <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                    <SelectTrigger className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="post-platform-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{platforms.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm">Date & time</Label>
                    <button type="button" onClick={suggestTime} disabled={suggesting} className="text-xs text-primary flex items-center gap-1" data-testid="suggest-time-btn">
                      {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Best time
                    </button>
                  </div>
                  <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                    className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="post-datetime-input" />
                </div>
              </div>
              <div>
                <Label className="text-sm">Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. GRWM: winter skincare"
                  className="mt-1.5 h-11 bg-transparent border-white/10" data-testid="post-title-input" />
              </div>
              <div>
                <Label className="text-sm">Notes / script</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Hook, beats, caption…"
                  className="mt-1.5 bg-transparent border-white/10 min-h-[80px]" data-testid="post-notes-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-full bg-primary text-background font-bold" data-testid="post-save-btn">Schedule</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardShell>
  );
};

export default Calendar;
